import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AppTooltip from '@/components/app-tooltip/AppTooltip';
import FileAttachment, { FILE_ATTACHMENT_STATUS } from '@components/form-attachment/FileAttachment';

import useMutation from '@/hooks/useMutation';

import { API } from '@/utils/constants/Apis';
import { FORMAT_DATE_EN_HH_MM, LOCALSTORAGE } from '@/utils/constants/AppConstants';
import { DataViewer } from '@/utils/helpers/common';
import { handleDownloadSingleFile } from '@/utils/helpers/fileHelper';

import { IComment, IFile } from '../models';
import EditButton from './EditButton';
import FoldedHandIcon from './FoldedHandIcon';
import LikeIcon from './LikeIcon';
import ReactButton from './ReactButton';

import '../styles/CommentBox.scss';

export const COMMENT_TYPE = {
  OTHER: 'other',
  ME: 'me'
} as const;
export type ICommentType = (typeof COMMENT_TYPE)[keyof typeof COMMENT_TYPE];

export interface ICommentProps {
  comment: IComment;
  onClickEditBtn: (comment: IComment) => void;
  msgErr?: string;
}

const CommentBox = (props: ICommentProps) => {
  const { comment, msgErr, onClickEditBtn } = props;
  const { t } = useTranslation();
  const [embedImage, setEmbedImage] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const files = comment?.attachments ?? [];
  const currentUserName = useMemo(() => {
    try {
      const localStorageUser = localStorage.getItem(LOCALSTORAGE.USER);
      return localStorageUser ? JSON.parse(localStorageUser)?.name : undefined;
    } catch {
      return undefined;
    }
  }, []);

  const initialReactionCounts = useMemo(() => {
    const reacts = comment?.reacts ?? [];
    const validReacts = reacts.filter((item) => !item.deleted && (item.reactType === 'like' || item.reactType === 'folded-hand'));
    return {
      like: validReacts.filter((item) => item.reactType === 'like').length,
      'folded-hand': validReacts.filter((item) => item.reactType === 'folded-hand').length
    } as const;
  }, [comment?.reacts]);

  const currentUserReactions = useMemo<('like' | 'folded-hand')[]>(() => {
    if (!currentUserName) return [];
    return (
      comment?.reacts
        ?.filter((item) => !item.deleted && item.userReactName === currentUserName)
        ?.map((item) => item.reactType as 'like' | 'folded-hand') ?? []
    ).filter((reaction, index, self) => self.indexOf(reaction) === index);
  }, [comment?.reacts, currentUserName]);

  const [reactionCounts, setReactionCounts] = useState(initialReactionCounts);
  const [userReactions, setUserReactions] = useState<('like' | 'folded-hand')[]>(currentUserReactions);
  const initialReactionUsers = useMemo(() => {
    const reacts = comment?.reacts ?? [];

    const validReacts = reacts.filter((item) => !item.deleted);

    const getUsersByReaction = (reaction: 'like' | 'folded-hand') => {
      return validReacts
        .filter((item) => item.reactType === reaction)
        .map((item) => item.userReactName)
        .filter(Boolean)
        .filter((name, index, self) => self.indexOf(name) === index)
        .join(', ');
    };

    return {
      like: getUsersByReaction('like'),
      'folded-hand': getUsersByReaction('folded-hand')
    };
  }, [comment?.reacts, currentUserName]);
  const [reactionUsers, setReactionUsers] = useState(initialReactionUsers);

  useEffect(() => {
    setReactionCounts(initialReactionCounts);
    setUserReactions(currentUserReactions);
    setReactionUsers(initialReactionUsers);
  }, [initialReactionCounts, currentUserReactions]);

  const { mutate: reactCommentsAPI } = useMutation(API.REACT_COMMENT_TASK_OF_PROJECT(comment.id), {
    method: 'POST',
    showToastError: true,
    showToastSuccess: false
  });

  useEffect(() => {
    if (!comment.content) return;
    setEmbedImage(comment.content.includes('embed-attachment-container'));
  }, [comment]);

  const contentLength = DataViewer.isEmptyAfterTrim(comment?.content);
  const createdName = comment.updatedStatus ? comment?.createdName : `(${t('account_list:status_account:inactive')}) ${comment?.createdName}`;

  const commentNoFile = (!!contentLength || embedImage) && files.length;
  const commentOnlyText = (!!contentLength || embedImage) && !files.length;

  const onDownloadFile = (file: Partial<IFile>) => {
    return handleDownloadSingleFile(file?.blobPath ?? '', file?.name ?? '');
  };

  const handleReactionClick = async (reaction: 'like' | 'folded-hand') => {
    const hasReacted = userReactions.includes(reaction);

    const nextReactionCounts = {
      ...reactionCounts,
      [reaction]: reactionCounts[reaction] + (hasReacted ? -1 : 1)
    } as const;

    const nextUserReactions = hasReacted ? userReactions.filter((item) => item !== reaction) : [...userReactions, reaction];

    const currentUsers = reactionUsers[reaction] ? reactionUsers[reaction].split(', ') : [];

    const nextUsers = hasReacted
      ? currentUsers.filter((name) => name !== currentUserName)
      : currentUserName
        ? [...currentUsers, currentUserName]
        : currentUsers;

    const nextReactionUsers = {
      ...reactionUsers,
      [reaction]: Array.from(new Set(nextUsers)).join(', ')
    };

    setReactionCounts(nextReactionCounts);
    setUserReactions(nextUserReactions);
    setReactionUsers(nextReactionUsers);

    try {
      await reactCommentsAPI({ reactType: reaction });
    } catch (error) {
      setReactionCounts(reactionCounts);
      setUserReactions(userReactions);
      setReactionUsers(reactionUsers);
    } finally {
      setTooltipOpen(false);
    }
  };

  return (
    <div
      id={comment.id ?? ''}
      className={classNames('comment-box group', comment?.type === COMMENT_TYPE.ME ? 'comment-box--right' : 'comment-box--left')}
    >
      <div className='comment-box__header'>
        <div className='comment-box__user-name text-textGray text-12'>
          {comment?.createdName.length > 12 ? (
            <AppTooltip className='text-textGray text-12' title={createdName}>
              {!comment.updatedStatus && <span className='text-12'>({t('account_list:status_account:inactive')}) </span>}
              {DataViewer.display(comment?.createdName.slice(0, 12))}...
            </AppTooltip>
          ) : (
            <>
              {!comment.updatedStatus && <span className='text-12'>({t('account_list:status_account:inactive')}) </span>}
              {DataViewer.display(comment?.createdName)}
            </>
          )}
        </div>
        {comment?.edited && (
          <div className='comment-box__edited text-12 body-400 text-textGray mr-[8px] shrink-0'>{t('widgets:comment_function:comment_edited')}</div>
        )}
        <div className='comment-box__created-date'>{DataViewer.localTimeBy(comment?.editedDate ?? comment?.createdDate, FORMAT_DATE_EN_HH_MM)}</div>
      </div>
      <div>
        <div className={classNames('comment-box__content', comment?.type === COMMENT_TYPE.OTHER && 'flex flex-row-reverse gap-1 !justify-end')}>
          <div className='flex items-center comment-box__edit-button mt-auto h-[29px] gap-1 mr-[2px]'>
            <AppTooltip
              title={
                <div className='flex w-auto items-center gap-1'>
                  <FoldedHandIcon onClick={() => handleReactionClick('folded-hand')} />
                  <LikeIcon onClick={() => handleReactionClick('like')} />
                </div>
              }
              trigger='click'
              placement='top'
              color='#FFFFFF'
              open={tooltipOpen}
              onOpenChange={setTooltipOpen}
            >
              <span>
                <ReactButton onClick={() => setTooltipOpen((prev) => !prev)} showAlways={tooltipOpen} />
              </span>
            </AppTooltip>
            {comment?.type === COMMENT_TYPE.ME && <EditButton onClick={() => onClickEditBtn(comment)} showAlways={tooltipOpen} />}
          </div>
          <div
            className={classNames(
              'rounded-[8px]',
              commentNoFile && (comment?.type === COMMENT_TYPE.ME ? 'bg-green10' : 'bg-gray1'),
              commentNoFile && 'p-[8px]'
            )}
          >
            {(!!contentLength || embedImage) && (
              <div
                className={classNames(
                  commentOnlyText && 'p-[8px]',
                  commentOnlyText && (comment?.type === COMMENT_TYPE.ME ? 'bg-green10' : 'bg-gray1'),
                  'rounded-[8px] break-all w-fit text whitespace-pre-wrap'
                )}
                dangerouslySetInnerHTML={{ __html: DataViewer.displayAsSanitizeHTML(comment?.content, ' ') }}
              />
            )}

            {files.map((file: Partial<IFile>) => (
              <div key={file.id} className={classNames('comment-box__attachment mb-[4px] [&:last-child]:mb-0', commentNoFile && 'mt-[10px]')}>
                <FileAttachment
                  className=' bg-white'
                  label={file?.name ?? ''}
                  status={FILE_ATTACHMENT_STATUS.VIEW}
                  onDownload={() => onDownloadFile(file)}
                  msgErr={msgErr}
                />
              </div>
            ))}
          </div>
        </div>
        {reactionCounts.like > 0 || reactionCounts['folded-hand'] > 0 ? (
          <div className={`mt-2 flex ${comment?.type === COMMENT_TYPE.ME ? 'justify-end' : 'justify-start'} items-center gap-2`}>
            {(['like', 'folded-hand'] as const).map((reaction) => {
              const count = reactionCounts[reaction];
              if (count === 0) return null;
              return (
                <AppTooltip className='text-textGray text-12' title={reaction === 'like' ? reactionUsers.like : reactionUsers['folded-hand']}>
                  <div
                    key={reaction}
                    className='flex items-center gap-1 rounded-md bg-gray1 px-2 py-1 shadow cursor-pointer hover:opacity-70 transition-opacity'
                    onClick={() => handleReactionClick(reaction)}
                  >
                    {reaction === 'like' ? <LikeIcon className='w-6 h-4' /> : <FoldedHandIcon className='w-6 h-4' />}

                    <span className={`text-14 ${userReactions.includes(reaction) ? 'text-link' : 'text-lnk'} font-medium`}>{count}</span>
                  </div>
                </AppTooltip>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default CommentBox;
